import {
  DossDataFormat,
  formatDate,
  formatDateTimeByLocale,
  formatNumberByLocale,
} from '@doss/common-all'
import { TableCell, TableRow } from '@mui/material'
import { Fragment } from 'react'
import { tss } from 'tss-react'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { BulkUpdateTableCell, BulkUpdateTableRow } from '~/common/utils/csv'
import { Text } from '~/components/ui/atoms/text/Text'

interface TableBulkUploadRowProps {
  row: BulkUpdateTableRow
  /** When set, user column previews show org member names (from getActiveUsers) instead of raw IDs */
  resolveUserDisplay?: (userId: string) => string
}

/**
 * Renders a single row to display to represent a row from a user's csv upload
 */
export function TableBulkUploadRow({ row, resolveUserDisplay }: TableBulkUploadRowProps) {
  const { classes } = useStyles()
  const { locale } = useGlobalContext()

  const formatUserIdsForPreview = (userIds: string[]): string => {
    if (resolveUserDisplay) {
      return userIds.map((id) => resolveUserDisplay(id)).join(', ')
    }
    return userIds.join(', ')
  }

  const renderTableCell = (cellData: BulkUpdateTableCell | undefined): React.ReactNode => {
    if (!cellData || !cellData.data) {
      return ''
    }

    // Ensure the data is handled according to its type
    switch (cellData.data.type) {
      case DossDataFormat.STRING:
      case DossDataFormat.ENUM:
      case DossDataFormat.ID:
        // Directly return string data
        return cellData.data.data
      case DossDataFormat.NUMBER:
        if (cellData.data.data) {
          return formatNumberByLocale(cellData.data.data, locale)
        } else {
          return ''
        }
      case DossDataFormat.BOOLEAN:
        // Convert boolean to human-readable format
        return <Checkbox checked={cellData.data.data} disabled />
      case DossDataFormat.DATE: {
        if (cellData.data.data) {
          return formatDate(cellData.data.data, { localeCode: locale })
        } else {
          return ''
        }
      }
      case DossDataFormat.DATE_TIME: {
        if (cellData.data.data) {
          return formatDateTimeByLocale(cellData.data.data, locale)
        } else {
          return ''
        }
      }
      case DossDataFormat.ARRAY: {
        // Array-of-lookup / file / user data is stored in the lookupCellValues, documentIds,
        // userIds fields, cellData.data.data is always an empty array
        if (cellData.lookupCellValues && cellData.lookupCellValues.length > 0) {
          return cellData.lookupCellValues.join(', ')
        }
        if (cellData.documentIds && cellData.documentIds.length > 0) {
          return cellData.documentIds.join(', ')
        }
        if (cellData.userIds && cellData.userIds.length > 0) {
          return formatUserIdsForPreview(cellData.userIds)
        }
        return Array.isArray(cellData.data.data) ? cellData.data.data.join(', ') : ''
      }
      case DossDataFormat.LOOKUP:
        return cellData.lookupCellValues ? cellData.lookupCellValues.join(', ') : ''
      case DossDataFormat.FILE:
        return cellData.documentIds ? cellData.documentIds.join(', ') : ''
      case DossDataFormat.USER:
        return cellData.userIds?.length ? formatUserIdsForPreview(cellData.userIds) : ''
      case DossDataFormat.FORMULA:
      case DossDataFormat.FORMULA_NUMBER:
      case DossDataFormat.FORMULA_STRING:
      case DossDataFormat.FORMULA_DATE:
      case DossDataFormat.FORMULA_DATE_TIME:
        return ''
    }
  }

  return (
    <TableRow>
      <TableCell>{row.order}</TableCell>
      {row.cells.map((cell, cellIdx) => {
        return (
          <Fragment key={cellIdx}>
            <TableCell>
              <div className={classes.cell}>
                {renderTableCell(cell)}
                {cell.error && (
                  <Text color="danger" typography="helperText">
                    {cell.error}
                  </Text>
                )}
              </div>
            </TableCell>
          </Fragment>
        )
      })}
    </TableRow>
  )
}

const useStyles = tss.create({
  cell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
  },
})
